import React, { useState, useEffect } from 'react';
import {
  Pane,
  Button,
  Table,
  Badge,
  TextInputField,
  SelectField,
  Textarea,
  Dialog,
  toaster,
  Spinner,
  IconButton,
  TrashIcon,
  Text,
  Heading,
  Switch,
  Card,
  TickIcon,
  CrossIcon
} from 'evergreen-ui';
import { useProject } from '../context/ProjectContext';
import api from '../services/api';

const TYPE_COLORS = {
  domain: 'blue',
  wildcard_domain: 'purple',
  ip: 'green',
  cidr: 'orange'
};

const TYPE_LABELS = {
  domain: 'Domain',
  wildcard_domain: 'Wildcard',
  ip: 'IP',
  cidr: 'CIDR'
};

function Scope() {
  const { currentProject } = useProject();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [showCheckDialog, setShowCheckDialog] = useState(false);
  const [newItem, setNewItem] = useState({ type: 'domain', value: '', description: '' });
  const [bulkInput, setBulkInput] = useState('');
  const [checkValue, setCheckValue] = useState('');
  const [checkResult, setCheckResult] = useState(null);

  useEffect(() => {
    if (currentProject?.id) {
      fetchItems();
    }
  }, [currentProject?.id]);

  const fetchItems = async () => {
    if (!currentProject?.id) return;
    try {
      setLoading(true);
      const response = await api.get(`/scope/${currentProject.id}`);
      setItems(response.data);
    } catch (error) {
      toaster.danger('Failed to fetch scope items');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newItem.value.trim()) {
      toaster.warning('Value is required');
      return;
    }
    try {
      const response = await api.post(`/scope/${currentProject.id}`, newItem);
      setItems([response.data, ...items]);
      setNewItem({ type: 'domain', value: '', description: '' });
      setShowAddDialog(false);
      toaster.success('Scope item added');
    } catch (error) {
      if (error.response?.status === 409) {
        toaster.warning('This scope item already exists');
      } else {
        toaster.danger('Failed to add scope item');
      }
    }
  };

  const handleBulkAdd = async () => {
    if (!bulkInput.trim()) {
      toaster.warning('Enter at least one item');
      return;
    }
    try {
      const response = await api.post(`/scope/${currentProject.id}/bulk`, { items: bulkInput });
      setItems([...response.data, ...items]);
      setBulkInput('');
      setShowBulkDialog(false);
      toaster.success(`Added ${response.data.length} scope items`);
    } catch (error) {
      toaster.danger('Failed to bulk add scope items');
    }
  };

  const handleDelete = async (itemId) => {
    try {
      await api.delete(`/scope/${currentProject.id}/${itemId}`);
      setItems(items.filter(i => i.id !== itemId));
      toaster.success('Scope item deleted');
    } catch (error) {
      toaster.danger('Failed to delete scope item');
    }
  };

  const handleToggleActive = async (item) => {
    try {
      const response = await api.put(`/scope/${currentProject.id}/${item.id}`, {
        is_active: !item.is_active
      });
      setItems(items.map(i => i.id === item.id ? response.data : i));
    } catch (error) {
      toaster.danger('Failed to update scope item');
    }
  };

  const handleCheck = async () => {
    if (!checkValue.trim()) return;
    try {
      const response = await api.post(`/scope/${currentProject.id}/check`, { value: checkValue });
      setCheckResult(response.data);
    } catch (error) {
      toaster.danger('Failed to check scope');
    }
  };

  if (!currentProject) {
    return (
      <Pane display="flex" alignItems="center" justifyContent="center" height="80vh">
        <Heading color="muted">Select a project first</Heading>
      </Pane>
    );
  }

  return (
    <Pane padding={24} maxWidth={1200} marginX="auto">
      <Pane display="flex" alignItems="center" justifyContent="space-between" marginBottom={24}>
        <Heading size={700}>Scope</Heading>
        <Pane display="flex" gap={8}>
          <Button onClick={() => setShowCheckDialog(true)}>
            Check Scope
          </Button>
          <Button onClick={() => setShowBulkDialog(true)}>
            Bulk Add
          </Button>
          <Button appearance="primary" onClick={() => setShowAddDialog(true)}>
            Add Item
          </Button>
        </Pane>
      </Pane>

      {/* Stats */}
      <Pane display="flex" gap={16} marginBottom={24}>
        <Card padding={16} background="tint1" flex={1}>
          <Text size={300} color="muted">Total Items</Text>
          <Heading size={600}>{items.length}</Heading>
        </Card>
        <Card padding={16} background="tint1" flex={1}>
          <Text size={300} color="muted">Domains</Text>
          <Heading size={600}>{items.filter(i => i.type === 'domain').length}</Heading>
        </Card>
        <Card padding={16} background="tint1" flex={1}>
          <Text size={300} color="muted">Wildcards</Text>
          <Heading size={600}>{items.filter(i => i.type === 'wildcard_domain').length}</Heading>
        </Card>
        <Card padding={16} background="tint1" flex={1}>
          <Text size={300} color="muted">IPs / CIDRs</Text>
          <Heading size={600}>{items.filter(i => i.type === 'ip' || i.type === 'cidr').length}</Heading>
        </Card>
      </Pane>

      {loading ? (
        <Pane display="flex" justifyContent="center" padding={40}>
          <Spinner />
        </Pane>
      ) : items.length === 0 ? (
        <Pane display="flex" flexDirection="column" alignItems="center" padding={40} background="tint1" borderRadius={8}>
          <Heading size={500} marginBottom={8}>No scope items defined</Heading>
          <Text color="muted" marginBottom={16}>Add domains, IPs, or CIDR ranges to define your project scope.</Text>
          <Button appearance="primary" onClick={() => setShowAddDialog(true)}>Add First Item</Button>
        </Pane>
      ) : (
        <Table>
          <Table.Head>
            <Table.TextHeaderCell flexBasis={100} flexShrink={0} flexGrow={0}>Type</Table.TextHeaderCell>
            <Table.TextHeaderCell>Value</Table.TextHeaderCell>
            <Table.TextHeaderCell>Description</Table.TextHeaderCell>
            <Table.TextHeaderCell flexBasis={80} flexShrink={0} flexGrow={0}>Active</Table.TextHeaderCell>
            <Table.TextHeaderCell flexBasis={80} flexShrink={0} flexGrow={0}>Actions</Table.TextHeaderCell>
          </Table.Head>
          <Table.Body>
            {items.map(item => (
              <Table.Row key={item.id}>
                <Table.Cell flexBasis={100} flexShrink={0} flexGrow={0}>
                  <Badge color={TYPE_COLORS[item.type] || 'neutral'}>
                    {TYPE_LABELS[item.type] || item.type}
                  </Badge>
                </Table.Cell>
                <Table.TextCell>
                  <Text fontFamily="mono">{item.value}</Text>
                </Table.TextCell>
                <Table.TextCell>{item.description || '-'}</Table.TextCell>
                <Table.Cell flexBasis={80} flexShrink={0} flexGrow={0}>
                  <Switch
                    checked={item.is_active}
                    onChange={() => handleToggleActive(item)}
                    height={20}
                  />
                </Table.Cell>
                <Table.Cell flexBasis={80} flexShrink={0} flexGrow={0}>
                  <IconButton
                    icon={TrashIcon}
                    intent="danger"
                    size="small"
                    onClick={() => {
                      if (window.confirm(`Delete scope item "${item.value}"?`)) {
                        handleDelete(item.id);
                      }
                    }}
                  />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {/* Add Single Item Dialog */}
      <Dialog
        isShown={showAddDialog}
        title="Add Scope Item"
        onCloseComplete={() => setShowAddDialog(false)}
        confirmLabel="Add"
        onConfirm={handleAdd}
      >
        <SelectField
          label="Type"
          value={newItem.type}
          onChange={e => setNewItem({ ...newItem, type: e.target.value })}
        >
          <option value="domain">Domain (e.g. example.com)</option>
          <option value="wildcard_domain">Wildcard Domain (e.g. *.example.com)</option>
          <option value="ip">IP Address (e.g. 192.168.1.1)</option>
          <option value="cidr">CIDR Range (e.g. 10.0.0.0/24)</option>
        </SelectField>
        <TextInputField
          label="Value"
          placeholder={
            newItem.type === 'domain' ? 'example.com' :
            newItem.type === 'wildcard_domain' ? '*.example.com' :
            newItem.type === 'ip' ? '192.168.1.1' :
            '10.0.0.0/24'
          }
          value={newItem.value}
          onChange={e => setNewItem({ ...newItem, value: e.target.value })}
          required
        />
        <TextInputField
          label="Description (optional)"
          placeholder="Notes about this scope item"
          value={newItem.description}
          onChange={e => setNewItem({ ...newItem, description: e.target.value })}
        />
      </Dialog>

      {/* Bulk Add Dialog */}
      <Dialog
        isShown={showBulkDialog}
        title="Bulk Add Scope Items"
        onCloseComplete={() => setShowBulkDialog(false)}
        confirmLabel="Add All"
        onConfirm={handleBulkAdd}
        width={600}
      >
        <Text size={300} color="muted" marginBottom={8}>
          Enter one item per line. Types are auto-detected: domains, *.wildcards, IPs, CIDR ranges.
        </Text>
        <Textarea
          placeholder={"example.com\n*.example.com\n192.168.1.0/24\n10.0.0.1"}
          value={bulkInput}
          onChange={e => setBulkInput(e.target.value)}
          rows={10}
          fontFamily="mono"
        />
      </Dialog>

      {/* Check Scope Dialog */}
      <Dialog
        isShown={showCheckDialog}
        title="Check Scope"
        onCloseComplete={() => { setShowCheckDialog(false); setCheckResult(null); setCheckValue(''); }}
        hasFooter={false}
      >
        <Pane display="flex" gap={8} marginBottom={16}>
          <TextInputField
            label="Value to check"
            placeholder="e.g. sub.example.com or 10.0.0.5"
            value={checkValue}
            onChange={e => { setCheckValue(e.target.value); setCheckResult(null); }}
            flex={1}
            marginBottom={0}
          />
          <Button appearance="primary" onClick={handleCheck} marginTop={24}>
            Check
          </Button>
        </Pane>
        {checkResult && (
          <Card
            padding={16}
            background={checkResult.in_scope ? 'greenTint' : 'redTint'}
            border={checkResult.in_scope ? '1px solid #52BD95' : '1px solid #D14343'}
            borderRadius={4}
          >
            <Pane display="flex" alignItems="center" gap={8}>
              {checkResult.in_scope ? (
                <Badge color="green">IN SCOPE</Badge>
              ) : (
                <Badge color="red">OUT OF SCOPE</Badge>
              )}
              <Text fontFamily="mono">{checkResult.value}</Text>
            </Pane>
            {checkResult.matched_rule && (
              <Text size={300} color="muted" marginTop={4}>
                Matched: {checkResult.matched_rule}
              </Text>
            )}
          </Card>
        )}
      </Dialog>
    </Pane>
  );
}

export default Scope;
